<template>
  <div class="page-container">
    <div class="page-content">
      <div class="content-wrapper">
        <div class="page-header">
          <div class="page-title">
            <h1>{{ isEdit ? $t('import_entities.edit') : $t('import_entities.create') }}</h1>
          </div>
        </div>

        <!-- Current File Info (for edit mode) -->
        <div v-if="isEdit && item && item.filename" class="card mb-3">
          <div class="card-body">
            <div class="row">
              <div class="col-md-8">
                <h6>{{ $t('import_entities.current_file') }}: <span class="text-info">{{ item.filename }}</span></h6>
                <div class="form-group mt-2">
                  <label>{{ $t('import_entities.description') }}</label>
                  <textarea
                    v-model="form.description"
                    class="form-control"
                    rows="2"
                    :placeholder="$t('import_entities.description_placeholder')"
                  ></textarea>
                </div>
              </div>
              <div class="col-md-4 text-right">
                <b-button variant="outline-secondary" @click="showFileUpload = !showFileUpload">
                  {{ showFileUpload ? $t('general.cancel') : $t('import_entities.replace_file') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- File Upload -->
        <div v-if="!isEdit || showFileUpload" class="card">
          <div class="card-body">
            <form @submit.prevent="uploadFile">
              <div class="form-group">
                <label>{{ isEdit ? $t('import_entities.new_file') : $t('import_entities.file') }}</label>
                <input
                  ref="fileInput"
                  type="file"
                  class="form-control"
                  :accept="'.xlsx,.xls'"
                  :required="!isEdit"
                  @change="handleFileChange"
                />
                <div class="form-text text-muted">
                  {{ $t('import_entities.file_help') }}
                </div>
              </div>

              <div class="form-group">
                <label>{{ $t('import_entities.description') }}</label>
                <textarea
                  v-model="form.description"
                  class="form-control"
                  rows="3"
                  :placeholder="$t('import_entities.description_placeholder')"
                ></textarea>
              </div>

              <button type="submit" class="btn btn-primary" :disabled="uploading">
                <span v-if="uploading" class="spinner-border spinner-border-sm mr-1"></span>
                {{ isEdit ? $t('import_entities.replace_and_reparse') : $t('import_entities.upload') }}
              </button>
              <b-button type="button" variant="light" class="ml-2" @click="cancel">
                {{ $t('general.cancel') }}
              </b-button>
            </form>
          </div>
        </div>

        <!-- Field Mapping Builder -->
        <div v-if="sheets && Object.keys(sheets).length > 0" class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5>{{ $t('import_entities.field_mapping') }}</h5>
            <div class="mapping-info">
              <b-badge variant="info" class="mr-2">
                {{ $t('import_entities.required_fields') }}: {{ requiredFieldsCount }}
              </b-badge>
              <b-badge :variant="isMappingComplete ? 'success' : 'warning'">
                {{ $t('import_entities.mapped_fields') }}: {{ mappedFieldsCount }}
              </b-badge>
            </div>
          </div>
          <div class="card-body">
            <field-mapping-builder
              v-model="form.mappings"
              :sheets="sheets"
              :model-fields="modelFields"
              @input="validateMappings"
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div v-if="sheets && Object.keys(sheets).length > 0" class="card">
          <div class="card-body">
            <button
              type="button"
              class="btn btn-success mr-2"
              :disabled="saving || !canImport"
              @click="saveAndImport"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
              {{ $t('import_entities.import') }}
            </button>
            <button
              type="button"
              class="btn btn-primary mr-2"
              :disabled="saving"
              @click="save"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
              {{ $t('general.save') }}
            </button>
            <router-link :to="{ name: 'import_entities' }" class="btn btn-default">
              {{ $t('general.cancel') }}
            </router-link>
          </div>
        </div>

        <!-- Status and Logs -->
        <div v-if="isEdit && item" class="card">
          <div class="card-header">
            <h6 class="card-title">{{ $t('import_entities.import_status') }}</h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <p><strong>{{ $t('import_entities.status') }}:</strong>
                  <span :class="getStatusClass(item.status)">
                    {{ $t(`import_entities.status_${item.status}`) }}
                  </span>
                </p>
                <p><strong>{{ $t('import_entities.created_at') }}:</strong> {{ formatDate(item.created_at) }}</p>
              </div>
              <div class="col-md-6">
                <p><strong>{{ $t('import_entities.filename') }}:</strong> {{ item.filename }}</p>
                <p v-if="item.completed_at"><strong>{{ $t('import_entities.completed_at') }}:</strong> {{ formatDate(item.completed_at) }}</p>
              </div>
            </div>

            <!-- Logs -->
            <div v-if="item.logs && item.logs.length" class="mt-3">
              <h6>{{ $t('import_entities.logs') }}</h6>
              <div class="logs-container" style="max-height: 300px; overflow-y: auto;">
                <div
                  v-for="log in item.logs"
                  :key="log.id"
                  :class="'log-entry log-' + log.level"
                >
                  <small>{{ formatDate(log.created_at) }}</small>
                  <span>{{ log.message }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import FieldMappingBuilder from './components/FieldMappingBuilder.vue'
import { ResourceFormMixin } from 'stylemix-vue-admin'

export default {
  name: 'ImportEntitiesForm',
  components: {
    FieldMappingBuilder,
  },
  mixins: [ResourceFormMixin],
  data() {
    return {
      uploading: false,
      saving: false,
      showFileUpload: false,
      sheets: null,
      modelFields: {},
      form: {
        file_id: null,
        filename: '',
        description: '',
        mappings: {},
      },
      item: null,
      requiredFieldsCount: 0,
      mappedFieldsCount: 0,
      isMappingComplete: false,
    }
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id
    },
    canImport() {
      return this.isMappingComplete && Object.keys(this.form.mappings).length > 0 && this.item.status !== 'processing'
    },
  },
  async created() {
    if (this.isEdit) {
      await this.loadItem()
    }
  },
  methods: {
    handleFileChange(event) {
      this.form.file = event.target.files[0]
    },
    async uploadFile() {
      if (!this.form.file) return

      this.uploading = true

      try {
        const formData = new FormData()
        formData.append('file', this.form.file)

        const response = await this.$http.post('import-entities/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        this.form.file_id = response.data.file_id
        this.form.filename = response.data.filename
        this.sheets = response.data.sheets

        // Load model fields for each sheet
        await this.loadModelFields()

        // Hide file upload form after successful upload
        if (this.isEdit) {
          this.showFileUpload = false
        }

      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('Upload error:', error)
        this.$toast.error('Upload failed: ' + (error.response?.data?.message || error.message))
      } finally {
        this.uploading = false
      }
    },
    async loadModelFields() {
      if (!this.sheets) return

      this.requiredFieldsCount = 0
      const fields = {}
      for (const sheetName of Object.keys(this.sheets)) {
        try {
          const response = await this.$http.get(`import-entities/model-fields/${sheetName}`)
          fields[sheetName] = response.data

          this.requiredFieldsCount += response.data.filter(f => f.required).length
        } catch (error) {
          /* eslint-disable-next-line no-console */
          console.warn(`Failed to load fields for ${sheetName}:`, error)
          this.$toast.error(`Failed to load fields for ${sheetName}`)
        }
      }
      this.modelFields = fields
      this.validateMappings()
    },
    validateMappings() {
      let mappedCount = 0
      let isComplete = true

      Object.keys(this.form.mappings || {}).forEach(sheetName => {
        const sheetFields = this.modelFields[sheetName] || []

        sheetFields.forEach(field => {
          const mappings = this.form.mappings[sheetName] && this.form.mappings[sheetName][field.name] || []

          if (field.required && mappings.length === 0) {
            isComplete = false
          }

          mappedCount += mappings.length
        })
      })

      this.mappedFieldsCount = mappedCount
      this.isMappingComplete = isComplete
    },
    async loadItem() {
      try {
        const response = await this.$http.get(`import-entities/${this.$route.params.id}`)
        this.item = response.data
        this.form = {
          ...this.form,
          ...this.item,
        }

        if (this.item.file_id) {
          try {
            const fileStructure = await this.$http.get(`import-entities/${this.item.id}/file-structure`)
            this.sheets = fileStructure.data.sheets
            await this.loadModelFields()
          } catch (error) {
            /* eslint-disable-next-line no-console */
            console.warn('Failed to load file structure:', error)
            if (this.item.mappings && Object.keys(this.item.mappings).length > 0) {
              await this.loadModelFieldsFromMappings()
            }
          }
        }

      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('Load item error:', error)
        this.$toast.error('Failed to load import configuration')
      }
    },
    async loadModelFieldsFromMappings() {
      const fields = {}
      const sheetsFromMappings = Object.keys(this.item.mappings || {})

      for (const sheetName of sheetsFromMappings) {
        try {
          const response = await this.$http.get(`import-entities/model-fields/${sheetName}`)
          fields[sheetName] = response.data

          this.requiredFieldsCount += response.data.filter(f => f.name.required).length
        } catch (error) {
          /* eslint-disable-next-line no-console */
          console.warn(`Failed to load fields for ${sheetName}:`, error)
        }
      }

      this.modelFields = fields
      this.validateMappings()
    },
    async save() {
      this.saving = true

      try {
        const data = {
          file_id: this.form.file_id,
          filename: this.form.filename,
          description: this.form.description,
          mappings: this.form.mappings,
        }

        let response
        if (this.isEdit) {
          response = await this.$http.put(`import-entities/${this.item.id}`, data)
        } else {
          response = await this.$http.post('import-entities', data)
        }

        this.item = response.data
        this.$toast.success('Import saved successfully')

        if (!this.isEdit) {
          this.$router.push({ name: 'import_entities' })
        }
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('Save error:', error)
        this.$toast.error('Save failed: ' + (error.response?.data?.message || error.message))
      } finally {
        this.saving = false
      }
    },
    async saveAndImport() {
      await this.save()
      if (this.item && this.item.id) {
        try {
          await this.$http.post(`import-entities/${this.item.id}/start-import`)
          this.$toast.success('Import started successfully')
        } catch (error) {
          /* eslint-disable-next-line no-console */
          console.error('Import start error:', error)
          this.$toast.error('Failed to start import',)
        }
      }
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm')
    },
    getStatusClass(status) {
      const classes = {
        pending: 'badge badge-warning',
        processing: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
      }
      return classes[status] || 'badge badge-secondary'
    },
    cancel() {
      this.$router.push({
        name: 'import_entities',
      })
    },
  },
}
</script>

<style scoped>
.form-actions {
  margin-top: 20px;
}

input[type="file"].form-control {
  height: 41px;
}

.mapping-info {
  display: flex;
  align-items: center;
}

.logs-container {
  border: 1px solid #dadada;
  padding: 10px 15px 10px 10px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3) inset;
}
.log-entry {
  padding: 8px;
  margin-bottom: 5px;
  border-radius: 4px;
  background-color: #e3f2fd;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #dadada;
}

.log-entry.log-info {
  background-color: #e3f2fd;
}

.log-entry.log-warning {
  background-color: #fff3e0;
}

.log-entry.log-error {
  background-color: #ffebee;
}

.log-entry small {
  color: #666;
  margin-right: 10px;
}
.badge {
  vertical-align: middle;
  margin-left: 5px;
}
</style>
